import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiArrowRight, FiEye, FiEyeOff, FiLock, FiMail } from "react-icons/fi";
import { AdminAuthContext } from "../../AdminAuthContext";
import DOMPurify from "dompurify";
import logo from "../../assets/images/Valley Logo.png";
import heroImage from "../../assets/images/Industrial.jpg";
import "./AdminLogin.css";

const AdminLogin = () => {
  const { login } = useContext(AdminAuthContext);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");

    const sanitizedEmail = DOMPurify.sanitize(email);
    const sanitizedPassword = DOMPurify.sanitize(password);

    if (!sanitizedEmail || !sanitizedPassword) {
      setError("Enter a valid email and password.");
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch("https://valleyapi-dr9y.onrender.com/api/admin-login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: sanitizedEmail, password: sanitizedPassword }),
      });

      const data = await response.json();
      if (response.ok) {
        login({ token: data.token, user: data.user });
        navigate("/admin", { replace: true });
      } else if (response.status === 400) {
        setError(data.details ? data.details.map((item) => item.msg).join(", ") : data.error || "Invalid input");
      } else if (response.status === 429) {
        setError("Too many login attempts. Please try again later.");
      } else if (response.status === 401) {
        setError("The email or password is incorrect.");
      } else {
        setError(data.error || "We could not sign you in.");
      }
    } catch (err) {
      console.error("Admin: Fetch error:", err.message);
      setError("We could not connect to the server. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="admin-login">
      <section className="admin-login__visual" style={{ backgroundImage: `url(${heroImage})` }}>
        <div className="admin-login__visual-content">
          <h1>Manage every order, product, and partner from one place.</h1>
          <p>Valley International Supply · Administrative workspace</p>
        </div>
      </section>

      <section className="admin-login__panel">
        <div className="admin-login__form-wrap">
          <img src={logo} alt="Valley International Supply" className="admin-login__logo" />
          <div className="admin-login__heading">
            <span>ADMIN PORTAL</span>
            <h2>Welcome back</h2>
            <p>Sign in with your administrator credentials.</p>
          </div>

          <form onSubmit={handleSubmit} className="admin-login__form">
            <label htmlFor="admin-email">Email address</label>
            <div className="admin-login__field">
              <FiMail aria-hidden="true" />
              <input
                id="admin-email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="name@company.com"
                autoComplete="email"
                required
              />
            </div>

            <label htmlFor="admin-password">Password</label>
            <div className="admin-login__field">
              <FiLock aria-hidden="true" />
              <input
                id="admin-password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                placeholder="Enter your password"
                autoComplete="current-password"
                required
              />
              <button type="button" onClick={() => setShowPassword((value) => !value)} aria-label={showPassword ? "Hide password" : "Show password"}>
                {showPassword ? <FiEyeOff /> : <FiEye />}
              </button>
            </div>

            {error && <div className="admin-login__error" role="alert">{error}</div>}

            <button className="admin-login__submit" type="submit" disabled={submitting}>
              <span>{submitting ? "Signing in..." : "Sign in to portal"}</span>
              {!submitting && <FiArrowRight aria-hidden="true" />}
            </button>
          </form>

          <p className="admin-login__help">Authorized personnel only. Contact your system administrator if you need access.</p>
        </div>
      </section>
    </main>
  );
};

export default AdminLogin;
